import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  V2AuthSurface,
  V2FormField,
  V2Button,
  Alert,
  LoadingState,
} from '@sakhtar/design-system/react';
import { apiPost } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { AUTH_BRAND_COPY, AUTH_HIGHLIGHTS, formatOrganizationDisplayLabel } from './authBrand.js';
import logoUrl from '@sakhtar/design-system/assets/logo/sakhtar-white-logo.svg';

type PreviewStatus = 'pending' | 'expired' | 'used' | 'invalid';
type Phase = 'checking' | PreviewStatus | 'submitting' | 'error';

interface PreviewResponse {
  readonly status: PreviewStatus;
  readonly organizationSlug: string;
}

interface ActivateResponse {
  readonly status: 'activated';
  readonly organizationSlug: string;
  readonly sessionId: string;
}

const PHASE_COPY: Record<Exclude<Phase, 'checking' | 'pending' | 'submitting'>, string> = {
  expired: 'این دعوت‌نامه منقضی شده — از مدیر سازمان دعوت جدید بخواهید.',
  used: 'این دعوت‌نامه قبلاً استفاده شده است.',
  invalid: 'لینک دعوت نامعتبر است.',
  error: 'مشکلی در بررسی دعوت‌نامه پیش آمد. دوباره تلاش کنید.',
};

/**
 * UI_REFERENCE_SOURCE:
 * section: Master Spec §09 Auth · Golden A — Invite Accept
 * canonical pattern: split-auth family aligned with Login, on the V2
 *   (`sk-*`) visual system — `V2AuthSurface`, same brand panel as
 *   LoginPage.tsx.
 * state reference: pending (form) · expired · used · invalid · error
 */
export function InviteAcceptPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('checking');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  // Stable per page-load so a retry of the same submission reuses the same
  // key rather than creating a second effect (AGENTS.md §9 — sensitive
  // mutations require idempotency).
  const idempotencyKey = useRef(crypto.randomUUID());

  useEffect(() => {
    if (!slug || !token) {
      setPhase('invalid');
      return;
    }
    apiPost<PreviewResponse>(`${tenantBasePath(slug)}/api/auth/invite/preview`, {
      inviteToken: token,
    })
      .then((res) => setPhase(res.status))
      .catch(() => setPhase('error'));
  }, [slug, token]);

  if (!slug) return null;

  const brandProps = {
    logo: <img src={logoUrl} alt="ساختار سیستم" height={28} />,
    brandHeadline: AUTH_BRAND_COPY.brandHeadline,
    brandTagline: AUTH_BRAND_COPY.brandTagline,
    highlights: AUTH_HIGHLIGHTS,
    brandFooter: AUTH_BRAND_COPY.brandFooter,
    tenantLabel: formatOrganizationDisplayLabel(slug),
  };

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!token) return;
    setPhase('submitting');
    setFormError(null);
    try {
      const result = await apiPost<ActivateResponse>(
        `${tenantBasePath(slug!)}/api/auth/invite/activate`,
        { inviteToken: token, password },
        { headers: { 'idempotency-key': idempotencyKey.current } },
      );
      navigate(`${tenantBasePath(result.organizationSlug)}/admin`);
    } catch {
      setFormError('فعال‌سازی حساب ناموفق بود — رمز عبور باید حداقل ۸ نویسه باشد.');
      setPhase('pending');
    }
  }

  if (phase === 'checking') {
    return (
      <V2AuthSurface {...brandProps} title="بررسی دعوت‌نامه" subtitle="">
        <LoadingState />
      </V2AuthSurface>
    );
  }

  if (phase !== 'pending' && phase !== 'submitting') {
    return (
      <V2AuthSurface {...brandProps} title="دعوت‌نامه" subtitle="">
        <Alert tone="warning">{PHASE_COPY[phase]}</Alert>
      </V2AuthSurface>
    );
  }

  return (
    <V2AuthSurface
      {...brandProps}
      title="تکمیل ثبت‌نام"
      subtitle="یک رمز عبور برای حساب خود تعیین کنید"
    >
      <form
        onSubmit={onSubmit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <V2FormField
          label="رمز عبور"
          required
          width="full"
          hint="حداقل ۸ نویسه"
          htmlFor="invite-password"
        >
          <input
            id="invite-password"
            className="sk-input"
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </V2FormField>
        {formError ? (
          <Alert tone="danger" title="خطا">
            {formError}
          </Alert>
        ) : null}
        <V2Button
          type="submit"
          variant="primary"
          disabled={phase === 'submitting'}
          style={{ width: '100%' }}
        >
          {phase === 'submitting' ? 'در حال فعال‌سازی…' : 'فعال‌سازی حساب'}
        </V2Button>
      </form>
    </V2AuthSurface>
  );
}
